// Who gets Kidase Premium, in one place.
//
// Premium = tester OR legacy Kidase subscription (users/{uid}.subscription)
//        OR Geezword account entitlement (entitlements/{uid}.kidase, written
//           only by the account functions in geezword-account).
//
// Builds read entitlementsTest only when built with VITE_BILLING_MODE=test
// (preview/test builds); anything else reads the live entitlements.

export const FREE_LESSON_COUNT = 7

// lessonIndex is the lesson's position in its full category (not a filtered list).
export function isFreeLesson(lessonIndex) {
  return Number.isInteger(lessonIndex) && lessonIndex >= 0 && lessonIndex < FREE_LESSON_COUNT
}

export const BILLING_MODE = import.meta.env?.VITE_BILLING_MODE === 'test' ? 'test' : 'live'
export const ENTITLEMENT_COLLECTION = BILLING_MODE === 'test' ? 'entitlementsTest' : 'entitlements'
export const ACCOUNT_URL = (import.meta.env?.VITE_ACCOUNT_URL || 'https://account.geezword.com').replace(/\/$/, '')

// Kidase Individual prices as sold on the account page (display only; Stripe is
// the source of truth at checkout, so these must be changed together).
export const KIDASE_PRICES = { monthly: '$3.99', annual: '$39.99' }

// Campaign tags (utm_*) a visitor arrived with, kept for this visit so a later
// subscription is credited to the campaign that brought them.
const ATTRIBUTION_KEY = 'sk_attribution'
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']

export function captureAttribution(search = globalThis.location?.search || '') {
  const params = new URLSearchParams(search)
  const utm = {}
  for (const k of UTM_KEYS) if (params.get(k)) utm[k] = params.get(k)
  try {
    if (Object.keys(utm).length) sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(utm))
  } catch { /* storage unavailable */ }
}

function storedAttribution() {
  try { return JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || '{}') } catch { return {} }
}

// The Geezword account page with the Kidase plan preselected. Arriving campaign
// tags win; otherwise the click is credited to the app and where it happened.
export function upgradeUrl(campaign = 'upgrade', stored = storedAttribution()) {
  const params = new URLSearchParams({ plan: 'kidase_individual_annual' })
  const utm = stored.utm_source ? stored : { utm_source: 'kidase-app', utm_medium: 'app', utm_campaign: campaign }
  for (const k of UTM_KEYS) if (utm[k]) params.set(k, utm[k])
  return `${ACCOUNT_URL}/?${params}`
}

// Where the header Upgrade button sends a customer.
export const UPGRADE_URL = `${ACCOUNT_URL}/?plan=kidase_individual_annual&utm_source=kidase-app&utm_medium=app&utm_campaign=upgrade`

const ACTIVE = ['active', 'trialing']
const DAY_MS = 24 * 60 * 60 * 1000
// A renewal can reach us a little after the period ends; keep access meanwhile.
const RENEWAL_GRACE_MS = 3 * DAY_MS

// How much grace this entitlement gets. The account functions stamp
// `graceDays: 0` when every purchase behind it is a free trial: a trial has no
// renewal to wait for, so the usual three days would quietly turn 30 into 33.
// Anything else — a subscription, or a trial alongside one — keeps the grace a
// paying customer is owed.
function graceMs(kidase) {
  return Number.isFinite(kidase?.graceDays) ? kidase.graceDays * DAY_MS : RENEWAL_GRACE_MS
}

export function entitlementActive(entitlementDoc, now = Date.now()) {
  const k = entitlementDoc?.kidase
  if (!k?.active) return false
  if (!k.until) return true
  const until = Date.parse(k.until)
  return !Number.isFinite(until) || until + graceMs(k) > now
}

// Whole days left before access ends, rounded up, never negative. Returns null
// when there is no end date (a lifetime teacher grant) or no entitlement.
export function daysRemaining(entitlementDoc, now = Date.now()) {
  const k = entitlementDoc?.kidase
  if (!k?.active || !k.until) return null
  const until = Date.parse(k.until)
  if (!Number.isFinite(until)) return null
  return Math.max(0, Math.ceil((until + graceMs(k) - now) / DAY_MS))
}

// A teacher grant redeemed with a code. The account functions mark it with
// `lifetime: true` and leave `until` empty, which entitlementActive() above
// already reads as permanent — this only identifies it so the account area can
// say so instead of showing a blank renewal date.
export function lifetimeTeacherAccess(entitlementDoc) {
  const k = entitlementDoc?.kidase
  return Boolean(k?.active && k.lifetime)
}

export function legacyActive(subscription) {
  return ACTIVE.includes(subscription?.status)
}

export function hasPremium({ isTester, subscription, entitlement, now = Date.now() }) {
  return Boolean(isTester || legacyActive(subscription) || entitlementActive(entitlement, now))
}
